import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { VehicleMultiFilter } from '../../components/fuel/VehicleMultiFilter';
import { MissingSlipDialog } from '../../components/fuel/MissingSlipDialog';
import { FleetBadge } from '../../components/fuel/FleetTicks';
import { MissingSlipsPanel } from '../../components/fuel/MissingSlipsPanel';
import { CheckCircle2, XCircle, Save, ClipboardCheck, Banknote, AlertTriangle, FilePlus2, Trash2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const money = (n) => `R ${Number(n || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const norm = (s) => (s || '').toString().trim().toUpperCase().replace(/\s+/g, '');

export default function FuelVerification({ month, headers, onEntriesChanged, fleet }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingAll, setSavingAll] = useState(false);
  const [slipOpen, setSlipOpen] = useState(false);
  // local edits per entry id: { verified_registration, verified_amount }
  const [edits, setEdits] = useState({});
  const [bankTotal, setBankTotal] = useState('');
  const [note, setNote] = useState('');
  const [vehicleFilter, setVehicleFilter] = useState([]);

  const fetchData = useCallback(async () => {
    if (!month) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/fuel-tracker/verification`, { headers, params: { month, fleet } });
      setData(r.data);
      const seed = {};
      (r.data.entries || []).forEach(e => {
        seed[e.id] = {
          verified_registration: e.verified_registration ?? '',
          verified_amount: e.verified_amount != null ? String(e.verified_amount) : '',
        };
      });
      setEdits(seed);
      setBankTotal(r.data?.totals?.bank_total ? String(r.data.totals.bank_total) : '');
      setNote(r.data?.reconciliation?.note || '');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load verification');
    } finally { setLoading(false); }
  }, [month, headers, fleet]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const updateLocal = (id, field, value) =>
    setEdits(prev => ({ ...prev, [id]: { ...(prev[id] || {}), [field]: value } }));

  const buildPayload = (entry) => {
    const local = edits[entry.id] || {};
    const va = local.verified_amount === '' || local.verified_amount == null ? null : parseFloat(local.verified_amount);
    if (va != null && !Number.isFinite(va)) return null;
    const regMatches = norm(local.verified_registration) === norm(entry.registration);
    const amtMatches = va != null && Math.abs(va - Number(entry.amount || 0)) < 0.01;
    return { verified_registration: local.verified_registration || '', verified_amount: va, verified: regMatches && amtMatches && va != null };
  };

  const isDirty = (entry) => {
    const l = edits[entry.id] || {};
    const savedAmt = entry.verified_amount != null ? String(entry.verified_amount) : '';
    return (l.verified_registration ?? '') !== (entry.verified_registration ?? '') || (l.verified_amount ?? '') !== savedAmt;
  };

  const saveRow = async (entry) => {
    const payload = buildPayload(entry);
    if (!payload) { toast.error('Invalid verified amount'); return; }
    try {
      await axios.post(`${API}/fuel-tracker/entries/${entry.id}/verify`, payload, { headers });
      await fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };

  const saveAll = async (rows) => {
    const dirty = rows.filter(isDirty);
    if (dirty.length === 0) { toast.info('No changes to save'); return; }
    if (dirty.some(e => !buildPayload(e))) { toast.error('Fix invalid verified amounts first'); return; }
    setSavingAll(true);
    const results = await Promise.allSettled(dirty.map(e =>
      axios.post(`${API}/fuel-tracker/entries/${e.id}/verify`, buildPayload(e), { headers })));
    const failed = results.filter(r => r.status === 'rejected').length;
    if (failed) toast.error(`${failed} of ${dirty.length} rows failed to save`);
    else toast.success(`Saved ${dirty.length} row${dirty.length > 1 ? 's' : ''}`);
    await fetchData();
    setSavingAll(false);
  };

  const deleteSlip = async (entry) => {
    if (!window.confirm(`Remove missing slip ${entry.registration} ${money(entry.slip_amount)}?`)) return;
    try {
      await axios.delete(`${API}/fuel-tracker/entries/${entry.id}`, { headers });
      toast.success('Missing slip removed');
      await fetchData();
      onEntriesChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  const saveReconciliation = async () => {
    const bt = parseFloat(bankTotal);
    if (!Number.isFinite(bt) || bt < 0) { toast.error('Enter a valid bank total'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/fuel-tracker/verification/reconciliation`, { month, bank_total: bt, note }, { headers });
      toast.success('Reconciliation saved');
      await fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  useEffect(() => { setVehicleFilter([]); }, [month]);

  const vehicles = useMemo(() => {
    const set = new Set();
    (data?.entries || []).forEach(e => { [e.registration, e.verified_registration].forEach(r => { if (norm(r)) set.add(norm(r)); }); });
    return [...set].sort();
  }, [data]);

  const entries = useMemo(() => {
    const all = data?.entries || [];
    if (vehicleFilter.length === 0) return all;
    return all.filter(e => vehicleFilter.includes(norm(e.registration)) || vehicleFilter.includes(norm(e.verified_registration)));
  }, [data, vehicleFilter]);

  // Derived totals from the CURRENT local edits (so user sees live differences)
  const derived = useMemo(() => {
    if (!data) return null;
    let vAmt = 0, cAmt = 0, matchesReg = 0, matchesAmt = 0, verifiedRows = 0;
    entries.forEach(e => {
      const l = edits[e.id] || {};
      const c = Number(e.amount || 0);
      const v = l.verified_amount === '' || l.verified_amount == null ? null : parseFloat(l.verified_amount);
      cAmt += c;
      if (v != null && Number.isFinite(v)) vAmt += v;
      const rMatch = l.verified_registration && norm(l.verified_registration) === norm(e.registration);
      const aMatch = v != null && Math.abs(v - c) < 0.01;
      if (rMatch) matchesReg += 1;
      if (aMatch) matchesAmt += 1;
      if (rMatch && aMatch) verifiedRows += 1;
    });
    return {
      captured_total: cAmt,
      verified_total: vAmt,
      difference: vAmt - cAmt,
      actual_fuel_usage: vAmt,
      matches_reg: matchesReg,
      matches_amt: matchesAmt,
      verified_rows: verifiedRows,
    };
  }, [data, edits, entries]);

  if (loading || !data) return <div className="text-center text-slate-500 py-10">Loading…</div>;

  const total = entries.length;
  const filtered = vehicleFilter.length > 0;
  const dirtyCount = entries.filter(isDirty).length;
  const balanced = derived && Math.abs(derived.difference) < 0.01 && derived.verified_rows === total && total > 0;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2" data-testid="verify-vehicle-filter-bar">
        <VehicleMultiFilter vehicles={vehicles} selected={vehicleFilter} onChange={setVehicleFilter} />
        {filtered && (
          <>
            <span className="text-sm text-slate-600" data-testid="verify-filter-summary">Showing {total} of {data.counts.total} entries for {vehicleFilter.join(', ')}</span>
            <Button variant="ghost" size="sm" onClick={() => setVehicleFilter([])} data-testid="verify-filter-clear">Clear</Button>
          </>
        )}
      </div>
      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        <Card className="bg-slate-100 border-slate-300"><CardContent className="p-3 text-center">
          <p className="text-xs font-bold text-slate-700 uppercase">Captured Total</p>
          <p className="text-xl font-black text-slate-800" data-testid="captured-total">{money(derived?.captured_total)}</p>
          <p className="text-xs text-slate-600">{total} entries</p>
        </CardContent></Card>
        <Card className="bg-emerald-50 border-emerald-200"><CardContent className="p-3 text-center">
          <p className="text-xs font-bold text-emerald-800 uppercase">Verified Total</p>
          <p className="text-xl font-black text-emerald-700" data-testid="verified-total">{money(derived?.verified_total)}</p>
          <p className="text-xs text-emerald-700">{derived?.verified_rows} rows fully OK</p>
        </CardContent></Card>
        <Card className={`${Math.abs(derived?.difference || 0) < 0.01 ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-300'}`}><CardContent className="p-3 text-center">
          <p className={`text-xs font-bold uppercase ${Math.abs(derived?.difference || 0) < 0.01 ? 'text-emerald-800' : 'text-amber-800'}`}>Difference</p>
          <p className={`text-xl font-black ${Math.abs(derived?.difference || 0) < 0.01 ? 'text-emerald-700' : 'text-amber-700'}`} data-testid="difference">{money(derived?.difference)}</p>
          <p className="text-xs">{Math.abs(derived?.difference || 0) < 0.01 ? 'No variance ✓' : 'Recheck rows'}</p>
        </CardContent></Card>
        <Card className="bg-blue-50 border-blue-200"><CardContent className="p-3 text-center">
          <p className="text-xs font-bold text-blue-800 uppercase">Actual Fuel Usage</p>
          <p className="text-xl font-black text-blue-700" data-testid="actual-usage">{money(derived?.actual_fuel_usage)}</p>
          <p className="text-xs text-blue-700">= Verified total</p>
        </CardContent></Card>
        <Card className="bg-purple-50 border-purple-200"><CardContent className="p-3 text-center">
          <p className="text-xs font-bold text-purple-800 uppercase">Bank Statement</p>
          <p className="text-xl font-black text-purple-700">{money(data.totals.bank_total)}</p>
          <p className="text-xs text-purple-700">{filtered || fleet ? 'Full month (all vehicles)' : 'Optional check'}</p>
        </CardContent></Card>
        <Card className={balanced ? 'bg-emerald-100 border-emerald-400' : 'bg-slate-100 border-slate-300'}><CardContent className="p-3 text-center">
          <p className="text-xs font-bold uppercase text-slate-700">Status</p>
          <p className={`text-xl font-black ${balanced ? 'text-emerald-700' : 'text-slate-700'}`}>{balanced ? 'BALANCED ✓' : 'IN PROGRESS'}</p>
          <p className="text-xs">Reg + Amount both match</p>
        </CardContent></Card>
      </div>

      {/* Bank reconciliation */}
      <Card className="bg-white border-slate-200">
        <CardHeader className="pb-2"><CardTitle className="text-slate-900 text-base flex items-center gap-2"><Banknote className="h-4 w-4 text-blue-600" /> Bank Statement (optional cross-check)</CardTitle></CardHeader>
        <CardContent className="grid md:grid-cols-4 gap-3">
          <div>
            <Label className="text-xs text-slate-600">Bank total for {month}</Label>
            <Input type="number" step="0.01" value={bankTotal} onChange={(e) => setBankTotal(e.target.value)} placeholder="0.00" data-testid="bank-total-input" />
          </div>
          <div className="md:col-span-2">
            <Label className="text-xs text-slate-600">Note</Label>
            <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Nedbank statement 30 Jun 2026" data-testid="reconciliation-note-input" />
          </div>
          <div className="flex items-end">
            <Button onClick={saveReconciliation} disabled={saving} className="w-full bg-blue-600 hover:bg-blue-700" data-testid="save-reconciliation-btn">
              <Save className="h-4 w-4 mr-1" /> {saving ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Recapture table */}
      <Card className="bg-white border-slate-200">
        <CardHeader className="pb-2 flex flex-row flex-wrap items-center justify-between gap-2 space-y-0">
          <CardTitle className="text-slate-900 text-base">Recapture — {month} ({total} entries{filtered ? ' · filtered' : ''})</CardTitle>
          <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => setSlipOpen(true)} className="border-rose-300 text-rose-700 hover:bg-rose-50" data-testid="add-missing-slip-btn">
            <FilePlus2 className="h-4 w-4 mr-1" /> Add Missing Slip
          </Button>
          {total > 0 && (
            <Button onClick={() => saveAll(entries)} disabled={savingAll || dirtyCount === 0} className="bg-emerald-600 hover:bg-emerald-700" data-testid="save-all-rows-btn">
              <Save className="h-4 w-4 mr-1" /> {savingAll ? 'Saving…' : `Save All${dirtyCount ? ` (${dirtyCount})` : ''}`}
            </Button>
          )}
          </div>
        </CardHeader>
        <MissingSlipDialog open={slipOpen} onOpenChange={setSlipOpen} month={month} vehicles={vehicles} onSaved={() => { fetchData(); onEntriesChanged?.(); }} />
        <CardContent>
          {total === 0 ? (
            <p className="text-center text-slate-500 py-6">{filtered ? 'No fuel entries for the selected vehicles.' : 'No fuel entries for this month.'}</p>
          ) : (
            <div className="overflow-x-auto border rounded">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-100">
                    <TableHead>Date</TableHead>
                    <TableHead>Captured Reg</TableHead>
                    <TableHead>Verified Reg</TableHead>
                    <TableHead className="text-right">Captured Amt</TableHead>
                    <TableHead className="text-right">Verified Amt</TableHead>
                    <TableHead className="text-right">Diff</TableHead>
                    <TableHead>Driver / Ref</TableHead>
                    <TableHead className="text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {entries.map((e) => {
                    const l = edits[e.id] || {};
                    const capReg = e.registration || '';
                    const verReg = l.verified_registration ?? '';
                    const regTouched = verReg.trim().length > 0;
                    const regOk = regTouched && norm(verReg) === norm(capReg);
                    const capAmt = Number(e.amount || 0);
                    const verAmtStr = l.verified_amount ?? '';
                    const verAmt = verAmtStr === '' ? null : parseFloat(verAmtStr);
                    const amtTouched = verAmt != null && Number.isFinite(verAmt);
                    const amtOk = amtTouched && Math.abs(verAmt - capAmt) < 0.01;
                    const diff = amtTouched ? verAmt - capAmt : 0;
                    const rowClass = regOk && amtOk ? 'bg-emerald-50/70' : (regTouched || amtTouched) ? 'bg-amber-50/60' : '';
                    return (
                      <TableRow key={e.id} className={rowClass} data-testid={`recapture-row-${e.id}`}>
                        <TableCell className="text-xs whitespace-nowrap">{e.date}</TableCell>
                        <TableCell className="font-mono text-sm font-bold">
                          {capReg} <FleetBadge fc={e.fleet_class} testId={`verify-fleet-${e.id}`} />
                          {e.missing_slip && <div className="mt-0.5"><span className="text-[10px] font-sans font-semibold px-1.5 py-0.5 rounded bg-rose-100 text-rose-700" data-testid={`missing-slip-badge-${e.id}`}>Missing slip</span></div>}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Input
                              value={verReg}
                              onChange={(ev) => updateLocal(e.id, 'verified_registration', ev.target.value)}
                              placeholder="Re-enter plate"
                              className="h-8 w-32 font-mono uppercase"
                              data-testid={`verify-reg-${e.id}`}
                            />
                            {regTouched && (regOk
                              ? <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                              : <XCircle className="h-4 w-4 text-rose-600" />)}
                          </div>
                        </TableCell>
                        <TableCell className="text-right font-semibold">{money(capAmt)}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1 justify-end">
                            <Input
                              type="number"
                              step="0.01"
                              value={verAmtStr}
                              onChange={(ev) => updateLocal(e.id, 'verified_amount', ev.target.value)}
                              placeholder="0.00"
                              className="h-8 w-28 text-right"
                              data-testid={`verify-amt-${e.id}`}
                            />
                            {amtTouched && (amtOk
                              ? <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                              : <XCircle className="h-4 w-4 text-rose-600" />)}
                          </div>
                        </TableCell>
                        <TableCell className={`text-right font-mono ${Math.abs(diff) < 0.01 ? 'text-slate-500' : diff < 0 ? 'text-rose-700' : 'text-amber-700'}`}>
                          {amtTouched ? money(diff) : '—'}
                        </TableCell>
                        <TableCell className="text-xs text-slate-600">
                          <div>{e.driver_name || '-'}</div>
                          <div className="font-mono text-[10px] text-slate-400">{e.reference || ''}</div>
                          {e.missing_slip && (e.litres || e.note) && <div className="text-[10px] text-rose-700">{e.litres ? `${e.litres} L` : ''}{e.litres && e.note ? ' · ' : ''}{e.note}</div>}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex gap-1 justify-end">
                            <Button size="sm" variant="outline" onClick={() => saveRow(e)} data-testid={`save-row-${e.id}`}>
                              <Save className="h-3 w-3 mr-1" /> Save
                            </Button>
                            {e.missing_slip && (
                              <Button size="sm" variant="outline" className="text-rose-600 px-2" onClick={() => deleteSlip(e)} data-testid={`delete-missing-slip-${e.id}`}>
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}

          {/* Footer notice */}
          {total > 0 && (
            <div className={`mt-3 rounded p-3 text-sm flex items-start gap-2 ${balanced ? 'bg-emerald-50 border border-emerald-200 text-emerald-900' : 'bg-amber-50 border border-amber-200 text-amber-900'}`}>
              {balanced
                ? <ClipboardCheck className="h-4 w-4 mt-0.5 shrink-0" />
                : <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />}
              <div>
                {balanced
                  ? <><b>All {total} entries verified.</b> Actual Fuel Usage for {month}{filtered ? ' (selected vehicles)' : ''} = <b>{money(derived?.actual_fuel_usage)}</b>.</>
                  : <><b>Recapture in progress:</b> {derived?.verified_rows}/{total} rows fully matched. Difference on captured amounts = <b>{money(derived?.difference)}</b>.</>}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <MissingSlipsPanel month={month} headers={headers} refreshKey={data} />
    </div>
  );
}
